/**
 * ChatbotWidget Component
 * Main entry point - FAB button + panel container
 * Manages open/close and fullscreen state
 */

import React, { useState, useEffect } from 'react';
import ChatbotPanel from './ChatbotPanel';
import './Chatbot.css';

/**
 * Chat bubble icon for FAB
 */
function ChatIcon() {
  return (
    <svg className="chatbot-fab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  );
}

/**
 * X icon for close state
 */
function XIcon() {
  return (
    <svg className="chatbot-fab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen, isOpen]);

  /**
   * Toggle panel open/close
   */
  const togglePanel = () => {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
    }
  };

  /**
   * Handle close with animation
   */
  const handleClose = () => {
    setIsFullscreen(false);
    setIsOpen(false);
  };

  /**
   * Toggle fullscreen mode
   */
  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  return (
    <>
      {/* Floating Action Button */}
      <button 
        className="chatbot-fab rf-bob-orb"
        onClick={togglePanel}
        type="button"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
      >
        {isOpen ? <XIcon /> : <ChatIcon />}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <ChatbotPanel
          isOpen={isOpen}
          isFullscreen={isFullscreen}
          onClose={handleClose}
          onToggleFullscreen={toggleFullscreen}
        />
      )}
    </>
  );
}

export default ChatbotWidget;
